import { create } from "zustand";
import { persist } from "zustand/middleware";
import { cartService } from "../services/cartService";
import { toast } from "react-toastify";

export type CartItem = {
  id: number | string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  type: 'product' | 'enclosure';
  raw?: any; // Optionally keep the raw item for advanced use
};

type CartState = {
  items: CartItem[];
  isLoading: boolean;
  error: string | null;
  tax: number;
  deliveryFee: number;
  addItem: (item: CartItem) => Promise<void>;
  removeItem: (id: number | string) => Promise<void>;
  updateQuantity: (id: number | string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  fetchCart: () => Promise<void>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
};

const toNumber = (value: unknown): number => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,
      error: null,
      tax: 0,
      deliveryFee: 0,

      fetchCart: async () => {
        try {
          set({ isLoading: true, error: null });

          // Check if user is logged in by looking for a token
          const token = localStorage.getItem('token');
          if (!token) {
            set({ isLoading: false });
            return;
          }

          const cartData = await cartService.getCart();

          // Transform API response to CartItem format (support both product and enclosure)
          const cartItems: CartItem[] = cartData.items.map(item => {
            if (item?.product) {
              return {
                id: item?.product?.id,
                name: item?.product?.name,
                image: item?.product?.featuredImage || (item?.product?.images && item?.product?.images[0]) || '/placeholder.svg',
                price: toNumber(item?.product?.price),
                quantity: toNumber(item?.quantity),
                type: 'product',
                raw: item,
              };
            } else if (item?.enclosure) {
              return {
                id: item?.enclosure?.id,
                name: item?.enclosure?.enclosure?.name || '',
                image: item?.enclosure?.svg || '',
                price: toNumber(item?.enclosure?.price?.total ?? item?.enclosure?.price),
                quantity: toNumber(item?.quantity),
                type: 'enclosure',
                raw: item
              };
            } else {
              return {
                id: Math.random().toString(36).substr(2, 9),
                name: 'Unknown',
                image: '/placeholder.svg',
                price: 0,
                quantity: toNumber(item?.quantity),
                type: 'product' as 'product',
                raw: item
              };
            }
          });


          set({ items: cartItems, isLoading: false, tax: toNumber(cartData.tax), deliveryFee: toNumber(cartData.deliveryFee) });
        } catch (error: any) {
          console.error("Error fetching cart:", error);

          // Handle authentication errors
          if (error?.response?.status === 401) {
            set({ isLoading: false, error: "Please log in to access your cart" });
          } else {
            set({ isLoading: false, error: "Failed to fetch cart" });
          }
        }
      },

      addItem: async (item) => {
        try {
          set({ isLoading: true, error: null });

          // Check if user is authenticated before adding to cart
          const token = localStorage.getItem('token');
          if (!token) {
            toast.error("Please login first to add items to cart", {
              position: "top-right",
              autoClose: 4000,
            });
            set({ isLoading: false });
            throw new Error("Authentication required");
          }

          // Add to backend
          await cartService.addToCart(item.id.toString(), item.quantity);

          // Fetch updated cart from backend
          await get().fetchCart();

          set({ isLoading: false });
        } catch (error: any) {
          console.error("Error adding item to cart:", error);
          set({ isLoading: false, error: error?.response?.data?.message || "Failed to add item to cart" });
          throw error;
        }
      },

      removeItem: async (id) => {
        try {
          set({ isLoading: true, error: null });

          // Remove from backend
          await cartService.removeFromCart(id.toString());

          // Fetch updated cart
          await get().fetchCart();

          set({ isLoading: false });
        } catch (error: any) {
          console.error("Error removing item from cart:", error);
          set({ isLoading: false, error: error?.response?.data?.message || "Failed to remove item from cart" });
          throw error;
        }
      },

      updateQuantity: async (id, quantity) => {
        try {
          set({ isLoading: true, error: null });

          if (quantity <= 0) {
            // If quantity is 0 or negative, remove the item
            await cartService.removeFromCart(id.toString());
          } else {
            // Update quantity
            await cartService.updateCartItem(id.toString(), quantity);
          }

          // Fetch updated cart
          await get().fetchCart();

          set({ isLoading: false });
        } catch (error: any) {
          console.error("Error updating cart quantity:", error);
          set({ isLoading: false, error: error?.response?.data?.message || "Failed to update cart" });
          throw error;
        }
      },

      clearCart: async () => {
        try {
          set({ isLoading: true, error: null });

          // Clear backend cart
          await cartService.clearCart();

          // Set local cart to empty
          set({ items: [], isLoading: false });
        } catch (error: any) {
          console.error("Error clearing cart:", error);
          set({ isLoading: false, error: error?.response?.data?.message || "Failed to clear cart" });
          throw error;
        }
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + toNumber(item.quantity), 0);
      },

      getTotalPrice: () => {
        return get().items.reduce(
          (total, item) => {
            const price = toNumber(item.price);
            const quantity = toNumber(item.quantity);
            return total + (item.type === 'enclosure' ? price : price * quantity);
          },
          0
        );
      },
    }),
    {
      name: "cart-storage", // name of the item in localStorage
      partialize: (state) => ({ items: state.items }), // only persist the items array
    }
  )
); 
